import EmblaCarousel, { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

interface EmblaSliderComponent {
	embla: EmblaCarouselType | null
	progress: HTMLElement | null
	init(wrapper: HTMLElement, options: EmblaOptionsType, autoplay?: boolean, progress?: boolean): void
	addTogglePrevNextBtnsActive(prevBtn: HTMLElement, nextBtn: HTMLElement): () => void
	addPrevNextBtnsClickHandlers(prevBtn: HTMLElement, nextBtn: HTMLElement): () => void
	addDotBtnsAndClickHandlers(dots: HTMLElement): () => void
	scrollTo(index: number): void
	onSlideChange(callback: (index: number) => void): void
	applyProgress(): void
	removeProgress(): void
}

export default {
	name: 'emblaSlider',
	component(): EmblaSliderComponent {
		return {
			embla: null,
			progress: null,

			init(wrapper: HTMLElement, options: EmblaOptionsType, autoplay: boolean = false, progress: boolean = false) {
				if (!wrapper) return

				const emblaNode = wrapper.querySelector<HTMLElement>('.oa-embla__viewport')

				if (!emblaNode) return

				const plugins = [WheelGesturesPlugin()]

				if (autoplay) {
					plugins.push(
						Autoplay({
							playOnInit: true,
							delay: 3000
						})
					)
				}

				this.embla = EmblaCarousel(emblaNode, options, plugins)
				this.progress = progress ? wrapper.querySelector<HTMLElement>('.oa-embla__progress__bar') : null

				// Initialiser le progress si présent
				if (this.progress) {
					this.applyProgress()
				}

				const prevBtnNode = wrapper.querySelector<HTMLElement>('.oa-embla__button--prev')
				const nextBtnNode = wrapper.querySelector<HTMLElement>('.oa-embla__button--next')

				if (prevBtnNode && nextBtnNode) {
					const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(prevBtnNode, nextBtnNode)
					this.embla?.on('destroy', removePrevNextBtnsClickHandlers)
				}

				const dots = wrapper.querySelector<HTMLElement>('.oa-embla__dots')
				if (dots) {
					const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(dots)
					this.embla?.on('destroy', removeDotBtnsAndClickHandlers)
				}

				// Mettre à jour le progress lors des événements de scroll
				if (this.progress) {
					this.embla?.on('init', () => this.applyProgress())
					this.embla?.on('reInit', () => this.applyProgress())
					this.embla?.on('select', () => this.applyProgress())
					this.embla?.on('scroll', () => this.applyProgress())
				}
			},

			onSlideChange(callback: (index: number) => void) {
				this.embla?.on('select', () => {
					callback(this.embla?.selectedScrollSnap() ?? 0)
				})
			},

			addTogglePrevNextBtnsActive(prevBtn, nextBtn) {
				const togglePrevNextBtnsState = () => {
					prevBtn.toggleAttribute('disabled', !this.embla?.canScrollPrev())
					nextBtn.toggleAttribute('disabled', !this.embla?.canScrollNext())
				}

				this.embla?.on('select', togglePrevNextBtnsState).on('init', togglePrevNextBtnsState).on('reInit', togglePrevNextBtnsState)

				return () => {
					prevBtn.removeAttribute('disabled')
					nextBtn.removeAttribute('disabled')
				}
			},

			addPrevNextBtnsClickHandlers(prevBtn, nextBtn) {
				const scrollPrev = () => this.embla?.scrollPrev()
				const scrollNext = () => this.embla?.scrollNext()

				prevBtn.addEventListener('click', scrollPrev)
				nextBtn.addEventListener('click', scrollNext)

				const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(prevBtn, nextBtn)

				return () => {
					removeTogglePrevNextBtnsActive()
					prevBtn.removeEventListener('click', scrollPrev)
					nextBtn.removeEventListener('click', scrollNext)
				}
			},

			addDotBtnsAndClickHandlers(dots) {
				let dotNodes: HTMLElement[] = []

				const addDotBtnsWithClickHandlers = () => {
					if (!this.embla) return
					const snapPoints = this.embla.scrollSnapList()
					// Responsive: hide dots when only one snap for current viewport
					if (snapPoints.length <= 1) {
						dots.innerHTML = ''
						dots.style.display = 'none'
						dotNodes = []
						return
					}

					// Ensure visible when multiple snaps
					dots.style.display = ''
					dots.innerHTML = snapPoints
						.map((_, index) => `<button aria-label="Slide ${index + 1}" class="oa-embla__dot" type="button"></button>`)
						.join('')

					dotNodes = Array.from(dots.querySelectorAll<HTMLElement>('.oa-embla__dot'))
					dotNodes.forEach((dotNode, index) => {
						dotNode.addEventListener('click', () => this.embla?.scrollTo(index))
					})
				}

				const toggleDotBtnsActive = () => {
					if (!dotNodes.length) return
					const previous = this.embla?.previousScrollSnap()
					const selected = this.embla?.selectedScrollSnap()
					dotNodes[previous ?? 0]?.classList.remove('oa-embla__dot--selected')
					dotNodes[selected ?? 0]?.classList.add('oa-embla__dot--selected')
				}

				this.embla
					?.on('init', addDotBtnsWithClickHandlers)
					.on('reInit', addDotBtnsWithClickHandlers)
					.on('init', toggleDotBtnsActive)
					.on('reInit', toggleDotBtnsActive)
					.on('select', toggleDotBtnsActive)

				return () => {
					dots.innerHTML = ''
				}
			},

			scrollTo(index: number) {
				this.embla?.scrollTo(index)
			},

			applyProgress() {
				if (!this.progress || !this.embla) return
				const progress = Math.max(0, Math.min(1, this.embla.scrollProgress()))
				this.progress.style.transform = `translate3d(${progress * 100}%,0px,0px)`
			},

			removeProgress() {
				if (!this.progress) return
				this.progress.removeAttribute('style')
			}
		}
	}
}
